import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAREDashboard, useCampaigns } from '../hooks/useARE'
import { PageHeader } from '@/shared/components/ui/PageHeader'
import { KpiCard } from '@/shared/components/ui/KpiCard'
import { EmptyState } from '@/shared/components/ui/EmptyState'
import { Button } from '@/shared/components/ui/Button'
import { Plus, ChevronRight, Zap, RefreshCw, Loader2, Check, AlertCircle, Database } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import api from '@/shared/lib/api'

/* eslint-disable @typescript-eslint/no-explicit-any */

function DetailRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-ink-border/20 last:border-0">
      <span className="text-[12px] text-ink-muted">{label}</span>
      <span className="text-[13px] font-semibold text-ink">{value}</span>
    </div>
  )
}

// ── CRM Sync Card ───────────────────────────────────────

function CRMSyncCard() {
  const [syncing, setSyncing] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [preview, setPreview] = useState<any>(null)

  const handlePreview = async () => {
    setPreviewing(true)
    setError(null)
    try {
      const { data } = await api.get('/are/crm/hubspot/preview')
      setPreview(data.data)
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur de connexion CRM')
    } finally {
      setPreviewing(false)
    }
  }

  const handleSync = async () => {
    setSyncing(true)
    setError(null)
    setResult(null)
    try {
      const { data } = await api.post('/are/crm/hubspot/sync')
      setResult(data.data)
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la synchronisation')
    } finally {
      setSyncing(false)
    }
  }

  return (
    <div className="bg-white rounded-card border border-ink-border p-5 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-brick-light flex items-center justify-center">
            <Database size={18} className="text-brick" />
          </div>
          <div>
            <h2 className="font-serif text-sm font-semibold text-ink">Synchronisation CRM</h2>
            <p className="text-[11px] text-ink-faint">Importez vos contacts depuis votre CRM connecté</p>
          </div>
        </div>
      </div>

      {/* Preview data */}
      {preview && !result && (
        <div className="bg-paper rounded-lg p-4 mb-4 animate-fade-in">
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center">
              <p className="text-lg font-bold text-ink">{preview.crm?.total_contacts ?? 0}</p>
              <p className="text-[10px] text-ink-faint uppercase tracking-wider">Contacts CRM</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-ink">{preview.crm?.total_deals ?? '—'}</p>
              <p className="text-[10px] text-ink-faint uppercase tracking-wider">Deals</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-ink">{preview.tremor?.already_imported ?? 0}</p>
              <p className="text-[10px] text-ink-faint uppercase tracking-wider">Déjà importés</p>
            </div>
          </div>
          {preview.crm?.sample_contacts?.length > 0 && (
            <div className="border-t border-ink-border/30 pt-3">
              <p className="text-[10px] text-ink-faint uppercase tracking-wider mb-2">Aperçu</p>
              {preview.crm.sample_contacts.map((c: any) => (
                <div key={c.id} className="flex items-center justify-between py-1">
                  <span className="text-[12px] text-ink">{c.name || c.email}</span>
                  <span className="text-[10px] text-ink-faint">{c.lifecycle || '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sync result */}
      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4 animate-fade-in">
          <div className="flex items-center gap-2 mb-2">
            <Check size={16} className="text-emerald-600" />
            <p className="text-sm font-medium text-emerald-700">Synchronisation terminée</p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[12px]">
            <div className="flex justify-between">
              <span className="text-emerald-600">Importés</span>
              <span className="font-semibold text-emerald-700">{result.contacts?.imported ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-emerald-600">Mis à jour</span>
              <span className="font-semibold text-emerald-700">{result.contacts?.updated ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-emerald-600">Scorés</span>
              <span className="font-semibold text-emerald-700">{result.contacts?.scored ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-emerald-600">Deals gagnés</span>
              <span className="font-semibold text-emerald-700">{result.deals?.found ?? 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
          <AlertCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-[12px] text-red-700">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button onClick={handlePreview} disabled={previewing}
          className="flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-ink-muted bg-paper-warm hover:bg-paper rounded-lg transition-colors disabled:opacity-50">
          {previewing ? <Loader2 size={14} className="animate-spin" /> : <Database size={14} />}
          Aperçu
        </button>
        <button onClick={handleSync} disabled={syncing}
          className="flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg transition-colors disabled:opacity-50">
          {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {syncing ? 'Synchronisation…' : 'Sync HubSpot'}
        </button>
        <a href="/settings/connectors"
          className="text-[11px] text-ink-faint hover:text-ink transition-colors ml-auto">
          Configurer →
        </a>
      </div>
    </div>
  )
}

// ── Main Page ───────────────────────────────────────────

export function AREDashboardPage() {
  const navigate = useNavigate()
  const { data: dash, isLoading } = useAREDashboard()
  const { data: campaigns } = useCampaigns()

  const d = dash as any

  const metrics = [
    { label: "Taux d'ouverture", value: `${d?.open_rate ?? 0}%`, sub: `${d?.total_opened ?? 0} ouverts` },
    { label: 'Taux de conversion', value: `${d?.conversion_rate ?? 0}%`, sub: 'envoi → avis publié' },
    { label: 'Feedbacks internes', value: d?.new_feedbacks ?? 0, sub: 'non traités' },
    { label: 'Campagnes actives', value: d?.active_campaigns ?? 0, sub: '' },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        moduleId="are"
        moduleFullName="Auto Reputation Engine"
        title="Dashboard"
        subtitle="Machine de croissance d'avis 5 étoiles"
        actions={<Button onClick={() => navigate('/are/campaigns/new')}><Plus size={16} /> Nouvelle campagne</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard label="Contacts CRM" value={d?.total_contacts ?? 0} accentColor="#E8562A" loading={isLoading}
          detailTitle="Contacts CRM — Détail"
          detailNode={
            <div className="space-y-1">
              <DetailRow label="Total contacts" value={d?.total_contacts ?? 0} />
              <DetailRow label="Promoteurs identifiés" value={d?.promoters ?? 0} />
              <DetailRow label="Déjà sollicités" value={d?.total_sent ?? 0} />
              <DetailRow label="Campagnes actives" value={d?.active_campaigns ?? 0} />
            </div>
          } />
        <KpiCard label="Promoteurs" value={d?.promoters ?? 0} accentColor="#1E6B3A" loading={isLoading}
          detailTitle="Promoteurs potentiels"
          detailNode={
            <div className="space-y-2">
              <DetailRow label="Promoteurs identifiés" value={d?.promoters ?? 0} />
              <DetailRow label="Contacts totaux" value={d?.total_contacts ?? 0} />
              <DetailRow label="Ratio promoteurs" value={d?.total_contacts > 0 ? `${((d?.promoters / d?.total_contacts) * 100).toFixed(0)}%` : '—'} />
              <div className="pt-2 mt-1 border-t border-ink-border/40">
                <p className="text-[11px] text-ink-faint">Clients récents, actifs et non sollicités. Prioritaires pour vos campagnes d'avis.</p>
              </div>
            </div>
          } />
        <KpiCard label="Emails envoyés" value={d?.total_sent ?? 0} accentColor="#E8562A" loading={isLoading}
          detailTitle="Pipeline d'envoi"
          detailNode={
            <div className="space-y-1">
              <DetailRow label="Emails envoyés" value={d?.total_sent ?? 0} />
              <DetailRow label="Ouverts" value={`${d?.total_opened ?? 0} (${d?.open_rate ?? 0}%)`} />
              <DetailRow label="Feedbacks reçus" value={d?.new_feedbacks ?? 0} />
              <DetailRow label="Avis publiés" value={d?.total_reviewed ?? 0} />
              <DetailRow label="Conversion" value={`${d?.conversion_rate ?? 0}%`} />
            </div>
          } />
        <KpiCard label="Avis générés" value={d?.total_reviewed ?? 0} accentColor="#C9A84C" loading={isLoading}
          detailTitle="Avis générés"
          detailNode={
            <div className="space-y-1">
              <DetailRow label="Avis publiés" value={d?.total_reviewed ?? 0} />
              <DetailRow label="Taux de conversion" value={`${d?.conversion_rate ?? 0}%`} />
              <DetailRow label="Emails envoyés" value={d?.total_sent ?? 0} />
              {campaigns?.length > 0 && (
                <>
                  <p className="text-[10px] font-medium tracking-wider uppercase text-ink-faint pt-3">Par campagne</p>
                  {campaigns.slice(0, 5).map((c: any) => (
                    <DetailRow key={c.id} label={c.name} value={`${c.total_reviewed ?? 0} avis`} />
                  ))}
                </>
              )}
            </div>
          } />
      </div>

      {/* CRM Sync Card */}
      <CRMSyncCard />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Metrics */}
        <div className="lg:col-span-2 bg-white rounded-card border border-ink-border p-5 shadow-card">
          <h2 className="font-serif text-sm font-semibold text-ink mb-4">Métriques de performance</h2>
          <div className="grid grid-cols-2 gap-4">
            {metrics.map((m) => (
              <div key={m.label} className="bg-paper rounded-lg p-4">
                <p className="text-[10px] tracking-[0.12em] uppercase text-ink-faint font-medium">{m.label}</p>
                <p className="text-kpi text-xl text-ink mt-1">{isLoading ? '…' : m.value}</p>
                {m.sub && <p className="text-[11px] font-light text-ink-faint mt-0.5">{m.sub}</p>}
              </div>
            ))}
          </div>
        </div>

        {/* Campaigns */}
        <div className="bg-white rounded-card border border-ink-border shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-ink-border/30">
            <h2 className="font-serif text-sm font-semibold text-ink">Campagnes</h2>
            <span className="text-[11px] text-ink-faint">{campaigns?.length ?? 0}</span>
          </div>
          <div className="divide-y divide-ink-border/30">
            {!campaigns?.length ? (
              <EmptyState
                compact
                title="Aucune campagne"
                description="Créez votre première campagne d'avis automatisée."
                action={{ label: 'Créer une campagne →', onClick: () => navigate('/are/campaigns/new'), variant: 'primary' }}
              />
            ) : (
              campaigns.slice(0, 5).map((c: Record<string, unknown>, idx: number) => (
                <button key={c.id as string}
                  onClick={() => navigate('/are/activity')}
                  className={cn('w-full flex items-center gap-3 px-5 py-3 hover:bg-gold-light/30 text-left transition-colors', idx % 2 === 1 && 'bg-paper')}>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-ink truncate">{c.name as string}</p>
                    <p className="text-[11px] font-light text-ink-faint">{c.total_sent as number} envois · {c.total_reviewed as number} avis</p>
                  </div>
                  <span className={cn('text-[10px] px-2.5 py-0.5 rounded-full font-medium',
                    c.status === 'active' ? 'bg-teal-light text-teal-dark' : 'bg-paper-warm text-ink-faint')}>
                    {c.status as string}
                  </span>
                  <ChevronRight size={13} className="text-ink-faint" />
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
